<!--
  Read-only document viewer with live SSE updates.

  Phase 1/2 scope (SPECIFICATION.md 16): it renders every block type the MCP
  layer can produce, including pending skeletons, and streams changes as the
  agent writes them. The ProseMirror editor and the soft-lock cursor claim are
  phase 3; the presence endpoint they use is already live.

  The one structural change from the old app.js is that the hand-rolled
  reconcile() is gone. It existed to stop `replaceChildren` on every event from
  throwing away text selection, focus on an option button and every rendered
  diagram; a keyed {#each} over the block ids gives Svelte the same information
  and it does the same job. Everything else here -- the generation ticket, the
  single-flight draw with its dirty flag, the rev/question comparison, the
  scroll anchor, the ticketed EventSource -- is carried over unchanged, because
  each of those exists for a failure that was actually observed.

  Comments live in CommentSidebar (TASK-041), which owns its own fetch: a
  `comment` or `comment_state` event bumps `commentsVersion` and never runs the
  document draw, since a comment changes no word of the document.

  ANCHORED COMMENTS. Selecting words in the document offers "Comment"
  (SelectionComment); choosing it hands the sidebar's composer the quote and
  the server's `{text, nth}` for it (lib/anchors.ts). The live anchors of the
  loaded threads are drawn as highlights through a context (Highlights) the
  renderer reads, and `rev` events ALSO bump `commentsVersion`: the server
  moves an anchor with the words it covers, or detaches it, in the same
  transaction as the edit (TASK-030), and announces only the `rev` -- so the
  comment list has to be re-read on a `rev` for the highlights to follow the
  text. Both fetches are made after the event, so both see the edit; until
  both have landed, an anchor whose offsets do not land on its own words is
  simply not drawn (`highlightRanges`).

  GAPS (TASK-044) ARE GONE, removed 2026-09-22. The ＋ controls between
  blocks let a reader ask for a paragraph to be written there; the
  whole-document editor made that redundant, because somebody who wants a
  paragraph there puts the caret there and writes it. Euan: "We can get rid
  of gap comments, but we need to do the node views for diagrams." Threads
  already anchored to a gap still load, remap and appear in the sidebar --
  only the way to make a NEW one has gone.

  DIAGRAMS (TASK-045). A selection inside a rendered diagram comments on the
  label selected in, or the whole diagram; the anchor is an ordinary one on
  its Mermaid source. MermaidBlock draws it by tinting the label or outlining
  the diagram, with `data-threads` like a highlight, so the click delegation,
  `markFor` and `reveal` below work unchanged. The sidebar is handed each
  diagram's source so it can show a label instead of Mermaid.

  PRESENCE (TASK-041 Phase 2). The viewers strip sits in the top bar (or, on
  a narrow screen, in a row under it), and the composer says whether an agent
  is watching. Both read `viewers`: the strip's first paint is a GET, and every
  `presence` event -- published on a join or a leave, never on a refresh, and
  carrying no names, since the stream is open to readers the strip is not --
  refetches it through the gated route. While the tab is visible it heartbeats
  every 15s with its own per-tab key, and says goodbye on hidden and pagehide.
  All of it is for people who may comment: below that, there is no strip.
-->
<script lang="ts">
  import { tick } from 'svelte'
  import { api, describeError, isAuthError, isMissingError } from '../lib/api'
  import { redirectToLogin } from '../lib/login'
  import { navigate } from '../lib/router.svelte'
  import { captureScroll, restoreScroll, type ScrollAnchor } from '../lib/scroll'
  import TopBar from '../lib/components/TopBar.svelte'
  import Block from '../lib/components/Block.svelte'
  import CommentSidebar from '../lib/components/CommentSidebar.svelte'
  import ShareDialog from '../lib/components/ShareDialog.svelte'
  import type { DocRow } from '../lib/components/doclist-types'
  import DocEditor from '../lib/editor/DocEditor.svelte'
  import { BlockLocks } from '../lib/editor/locks.svelte'
  import type { Op } from '../lib/editor/ops'
  import ViewersStrip from '../lib/components/ViewersStrip.svelte'
  import SelectionComment from '../lib/components/SelectionComment.svelte'
  import { me } from '../lib/me'
  import { gapThreads, highlightRanges, textBlocks, type AnchorDraft } from '../lib/anchors'
  import { Highlights, provideHighlights } from '../lib/highlights.svelte'
  import { tabSessionKey } from '../lib/presence'
  import type { CommentRow, DocDetail, NodeContent, PMNode, Question, Viewer } from '../lib/types'

  let { docId }: { docId: string } = $props()

  let title = $state('Loading…')
  let blocks = $state<PMNode[]>([])
  /**
   * The revision `blocks` came from. The editor adopts a new server document
   * only when this moves -- comparing arrays would re-seat it on every
   * redraw, and re-seating takes the caret with it.
   */
  let docRev = $state(0)
  let questions = $state<Question[]>([])
  let status = $state('connecting')
  let live = $state(false)
  let noTabs = $state(false)
  /** The caller's role on this document, from the doc fetch. */
  let role = $state<string | null>(null)
  /** Signed in with an agent's token (a developer bearer), not as a person. */
  let isAgent = $state(false)
  /**
   * Bumped on every `comment` / `comment_state` event and every (re)connect.
   * The sidebar refetches its threads on a bump; the document body does not
   * redraw at all, because a comment does not change a word of it. TASK-041.
   */
  let commentsVersion = $state(0)
  /**
   * Bumped by a save, to redraw without waiting for the event stream.
   *
   * The server publishes a `rev` event for our own write too, so the stream
   * would redraw eventually -- but "eventually" here means the read view shows
   * the text as it was for as long as the round trip takes, immediately after
   * the person watched themselves change it. That is the moment somebody
   * decides the editor does not work.
   */
  let localWrites = $state(0)

  /**
   * A handle on the draw loop, for callers outside the effect that owns it.
   *
   * Not a counter the effect watches: that effect depends on `docId` and owns
   * the event stream, so re-running it to repaint one block would tear down
   * and rebuild the SSE connection every time somebody pressed Done. Same
   * shape as TravelingDot's `rescan`.
   */
  let redraw: (() => void) | null = null
  const canComment = $derived(role === 'commenter' || role === 'editor' || role === 'owner')

  /* ------------------------------------------------------ editing (TASK-050)
   *
   * Editing needs `editor`, which is a strictly stronger grant than the one
   * that lets somebody comment. A public reader has no role at all, so this is
   * false for them by construction and none of it is drawn.
   */
  const canEdit = $derived(role === 'editor' || role === 'owner')

  /* -------------------------------------------------------------- sharing
   *
   * Owner only, because `POST /v1/docs/:id/shares` needs `own` and the access
   * list the dialog shows needs it too -- the same rule the document list
   * applies (`docActions`). A button that opens a dialog which then refuses
   * to load is worse than no button.
   */
  const canShare = $derived(role === 'owner')
  let sharing = $state(false)

  /**
   * What the share dialog needs, which is only `id` and `name`.
   *
   * It takes a `DocRow` -- the list's shape, with a folder, a revision and an
   * updated_at that this screen has no reason to know. The fields it does not
   * read are filled with what is true here rather than invented: the revision
   * we last drew, and no folder, because a document opened by URL was not
   * reached through one.
   */
  const shareDoc = $derived({
    id: docId,
    name: title,
    rev: docRev,
    updated_at: '',
    folderId: null,
    role: (role ?? 'viewer') as DocRow['role'],
  } satisfies DocRow)

  /** The cursor claim, and who else is holding what. */
  const locks = new BlockLocks()
  let editError = $state('')
  /** Ops refused because they would detach a thread, kept for "save anyway". */
  let detachPending = $state<Op[] | null>(null)

  $effect(() => {
    locks.attach(canEdit ? tabNodeId : null)
  })

  /**
   * The cursor moved to a different block: take that one, give the last back.
   *
   * The soft lock doing what it was designed for and never used for
   * (DECISIONS S15). It follows the cursor rather than a button, which is the
   * difference between claiming a paragraph being something a person does and
   * something that simply happens while they type.
   */
  function onCursorBlock(blockId: string | null) {
    if (!canEdit) return
    if (blockId) void locks.claim(blockId)
    else void locks.release()
  }

  /**
   * Send what the editor worked out.
   *
   * The ops carry their own guards: a `replace` carries the `ifHash` the
   * server last gave for that block, so an agent's write landing while
   * somebody typed is refused rather than overwritten.
   */
  async function sendOps(ops: Op[], allowDetach = false): Promise<boolean> {
    const nodeId = tabNodeId
    if (!nodeId || !ops.length) return true
    const body = allowDetach
      ? ops.map((o) => (o.op === 'insert' ? o : { ...o, allowDetach: true }))
      : ops
    try {
      await api(`/v1/nodes/${nodeId}/ops`, { method: 'POST', body: JSON.stringify({ ops: body }) })
      editError = ''
      detachPending = null
      localWrites += 1
      redraw?.()
      return true
    } catch (err) {
      const code = (err as any)?.code ?? (err as any)?.data?.code
      if (code === 'anchors_affected' && !allowDetach) {
        detachPending = ops
        editError =
          'Part of this edit touches a paragraph that has a comment on it. Saving may move the comment onto different words, or detach it.'
        return false
      }
      editError = describeSaveError(err)
      return false
    }
  }

  async function saveAnyway() {
    const pending = detachPending
    if (!pending) return
    detachPending = null
    await sendOps(pending, true)
  }

  function describeSaveError(err: unknown): string {
    const code = (err as any)?.code ?? (err as any)?.data?.code
    if (code === 'guard_mismatch') {
      return 'Somebody else changed this block while you were editing it. Their version is on the page; copy your words out before closing.'
    }
    if (code === 'being_edited') {
      return 'Somebody else has their cursor in one of those paragraphs. Your other changes were saved.'
    }
    if (code === 'access') return 'You do not have permission to edit this document.'
    return `Could not save: ${describeError(err)}.`
  }
  /** Who is reading: marks the comments that mention them. */
  let viewer = $state<{ userId: string; agentId: string | null } | null>(null)

  /*
   * TASK-049: a stranger reading a published document.
   *
   * The server has already decided this by the time the page exists -- the
   * shell guard bounces a signed-out visitor to /login unless the document is
   * public -- so arriving here with no session means exactly one thing, and
   * the app reads from the public surface instead of the ordinary one.
   *
   * `null` is "not known yet", and the draw waits for it. Guessing wrong in
   * either direction is a wasted request and a 403 in somebody's console.
   */
  let publicView = $state<boolean | null>(null)

  const whoami = me().then((m) => {
    isAgent = Boolean(m?.user?.agentId)
    if (m?.user) viewer = { userId: m.user.id, agentId: m.user.agentId ?? null }
    publicView = !m?.authenticated
    return m
  })

  /* ---------------------------------------------------------- presence */

  /**
   * Who is on the document, for the strip and the composer's line. `null` is
   * "not known": not loaded yet, or not this reader's to see (presence is
   * gated at `comment`, TASK-041 open question 6).
   */
  let viewers = $state<Viewer[] | null>(null)
  /** Bumped on every `presence` event and every (re)connect. */
  let presenceVersion = $state(0)

  /*
   * Every answer takes a ticket, and one that lands after a newer answer has
   * been drawn is dropped: a heartbeat's reply and an event's refetch can
   * cross, and the older one must not put back an agent that has left.
   */
  let issued = 0
  let applied = 0

  async function fetchViewers(request: () => Promise<{ present: Viewer[] }>) {
    const ticket = ++issued
    try {
      const res = await request()
      if (ticket > applied) {
        applied = ticket
        viewers = res.present ?? []
      }
    } catch {
      /* the strip is advisory: a failed fetch leaves the last answer up */
    }
  }

  $effect(() => {
    void presenceVersion
    const id = docId
    if (!canComment) return
    void fetchViewers(() => api<{ present: Viewer[] }>(`/v1/docs/${id}/presence`))
  })

  /*
   * The heartbeat. Every 15s while the tab is VISIBLE -- a tab in the
   * background is not someone looking -- and a DELETE the moment it is hidden
   * or unloaded, sent with `keepalive` so it survives the page going away. A
   * tab that never says goodbye fades from the strip within 45s.
   */
  $effect(() => {
    const id = docId
    if (!canComment) {
      viewers = null
      return
    }
    const key = tabSessionKey()
    let beat: ReturnType<typeof setInterval> | null = null
    let disposed = false

    const post = () =>
      fetchViewers(() =>
        api<{ present: Viewer[] }>(`/v1/docs/${id}/presence`, {
          method: 'POST',
          body: JSON.stringify({ sessionKey: key }),
        }),
      )

    const start = () => {
      if (beat || disposed) return
      void post()
      beat = setInterval(() => void post(), 15_000)
    }
    const stop = () => {
      if (!beat) return
      clearInterval(beat)
      beat = null
      api(`/v1/docs/${id}/presence?sessionKey=${encodeURIComponent(key)}`, {
        method: 'DELETE',
        keepalive: true,
      }).catch(() => {})
    }
    const onvisibility = () => (document.visibilityState === 'visible' ? start() : stop())
    // A page restored from the back/forward cache fires `pageshow`, not a
    // visibility change: it is looking again.
    const onpageshow = () => {
      if (document.visibilityState === 'visible') start()
    }

    document.addEventListener('visibilitychange', onvisibility)
    window.addEventListener('pagehide', stop)
    window.addEventListener('pageshow', onpageshow)
    if (document.visibilityState === 'visible') start()

    return () => {
      disposed = true
      stop()
      document.removeEventListener('visibilitychange', onvisibility)
      window.removeEventListener('pagehide', stop)
      window.removeEventListener('pageshow', onpageshow)
    }
  })

  /* ------------------------------------------------ anchored comments */

  /** The tab's content node; an anchor is resolved against it. */
  let tabNodeId = $state<string | null>(null)
  let commentRows = $state<CommentRow[]>([])
  /** Words chosen to comment on, waiting in the composer. */
  let pendingAnchor = $state<AnchorDraft | null>(null)
  let sidebar = $state<ReturnType<typeof CommentSidebar> | null>(null)

  const highlights = new Highlights((threadId) => {
    highlights.active = threadId
    void sidebar?.focusThread(threadId)
  })
  provideHighlights(highlights)

  /** Every block the server's anchor scan searches, in its order. */
  const scanned = $derived(textBlocks(blocks))
  /** Every diagram's Mermaid source, by block id: the sidebar never shows it (TASK-045). */
  const diagrams = $derived(
    new Map(
      scanned
        .filter((b) => b.node.type === 'codeBlock' && b.node.attrs?.language === 'mermaid')
        .map((b) => [b.id, b.raw]),
    ),
  )

  $effect(() => {
    const roots = commentRows.filter((r) => r.parent_utterance_id === null && r.kind === 'comment')
    highlights.ranges = highlightRanges(scanned, roots)
    highlights.gaps = gapThreads(roots)
  })

  const reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

  /** The first drawn piece of a thread's highlight, if it is on the page. */
  function markFor(threadId: string): HTMLElement | null {
    if (!bodyEl || !(window.CSS && CSS.escape)) return null
    return bodyEl.querySelector<HTMLElement>(`[data-threads~="${CSS.escape(threadId)}"]`)
  }

  /** Scroll a thread's words into view, but only if they are not in view. */
  async function reveal(threadId: string, force = false) {
    await tick()
    const el = markFor(threadId)
    if (!el) return
    const box = el.getBoundingClientRect()
    // The sticky top bar covers the first 4.25rem of the viewport.
    const top = 72
    if (!force && box.top >= top && box.bottom <= window.innerHeight) return
    el.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' })
  }

  /*
   * Hovering a thread lights its words at once, and scrolls to them only after
   * the pointer has rested a moment: a pointer passing over the list on its way
   * somewhere else should not drag the document around.
   */
  let dwell: ReturnType<typeof setTimeout> | null = null
  function onThreadActive(threadId: string | null, how: 'hover' | 'focus') {
    if (dwell) clearTimeout(dwell)
    dwell = null
    if (!threadId) {
      highlights.active = null
      return
    }
    highlights.active = threadId
    if (how === 'focus') void reveal(threadId)
    else dwell = setTimeout(() => void reveal(threadId), 250)
  }

  function showThread(threadId: string) {
    highlights.active = threadId
    void reveal(threadId, true)
  }

  function commentOn(draft: AnchorDraft) {
    pendingAnchor = draft
  }

  /*
   * Choosing a highlight with the pointer focuses its thread. Delegated from
   * the document body rather than put on each <mark>, so the marks stay plain
   * text semantics; the keyboard's way in is the pin button beside each range.
   * A click that ends a drag-selection, or lands on a link, is not a choice.
   */
  $effect(() => {
    const el = bodyEl
    if (!el) return
    const onclick = (e: MouseEvent) => {
      const target = e.target as Element | null
      const hit = target?.closest('[data-threads]')
      if (!hit || target?.closest('a')) return
      const sel = document.getSelection()
      if (sel && !sel.isCollapsed) return
      const ids = (hit.getAttribute('data-threads') ?? '').split(' ').filter(Boolean)
      if (!ids.length) return
      // Overlapping threads: each click moves on to the next one.
      const at = highlights.active ? ids.indexOf(highlights.active) : -1
      highlights.activate(ids[(at + 1) % ids.length])
    }
    el.addEventListener('click', onclick)
    return () => el.removeEventListener('click', onclick)
  })

  type Failure =
    | { kind: 'stuck'; message: string }
    | { kind: 'missing' }
    | { kind: 'error'; message: string }
  let failure = $state<Failure | null>(null)

  let bodyEl = $state<HTMLElement | null>(null)
  /** True once a draw has landed, so an empty document is not mistaken for a
      document that has not arrived. */
  let loaded = $state(false)

  /*
   * The scroll anchor and the diagram callback live at component scope rather
   * than inside the effect: a diagram finishes rendering long after the draw
   * that created it, and it needs the anchor that draw captured.
   */
  let anchor: ScrollAnchor | null = null

  /** Diagrams change height as they render, so re-apply the anchor after one
      paints. */
  function diagramSettled() {
    restoreScroll(bodyEl, anchor)
  }

  /*
   * Keys for the {#each}. Defensive against a duplicate id: Svelte throws on
   * one, and a malformed document should not blank the page.
   *
   * THIS USED TO INTERLEAVE GAP ZONES between the blocks -- the "+" controls
   * that let a reader comment on the space between two paragraphs (TASK-044).
   * Euan removed the feature on 2026-09-22, the whole-document editor having
   * made it redundant: "We can get rid of gap comments, but we need to do the
   * node views for diagrams." Somebody who wants a paragraph there now puts
   * the caret there and writes it.
   *
   * Threads already anchored to a gap are untouched: the server still stores,
   * remaps and lists them, and they still appear in the sidebar. What is gone
   * is the way to make a NEW one.
   */
  const keyed = $derived.by(() => {
    const seen = new Set<string>()
    return blocks.map((node, i) => {
      const raw = (node.attrs?.id as string | undefined) ?? `#${i}`
      let key = raw
      while (seen.has(key)) key = `${key}#${i}`
      seen.add(key)
      return { node, key }
    })
  })


  $effect(() => {
    const id = docId

    /* Everything below is deliberately NOT $state: it is bookkeeping for the
       draw loop, and making it reactive would re-run this effect. */
    let nodeId: string | null = null
    let lastRev = 0
    let lastQuestions = ''
    // Monotonic. Every draw takes a ticket and abandons its own result if a
    // newer draw started while it was waiting on the network.
    let generation = 0
    let inFlight: Promise<void> | null = null
    let dirty = false
    let disposed = false
    let events: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | null = null

    async function drawOnce() {
      const gen = ++generation
      // Settled before the first request, not raced with it.
      await whoami
      if (gen !== generation || disposed) return
      const pub = publicView === true
      const doc = await api<DocDetail>(pub ? `/v1/public/docs/${id}` : `/v1/docs/${id}`)
      if (gen !== generation || disposed) return
      title = doc.name
      document.title = doc.name
      role = doc.role ?? null
      nodeId = doc.files[0]?.content?.id ?? null
      tabNodeId = nodeId
      if (!nodeId) {
        noTabs = true
        return
      }
      noTabs = false

      const [node, questionsRes] = await Promise.all([
        api<NodeContent>(pub ? `/v1/public/nodes/${nodeId}/content` : `/v1/nodes/${nodeId}/content`),
        // Questions are an agent asking the people who own the document
        // something. A stranger is not one of those people and the endpoint
        // would refuse them, so it is not asked.
        pub
          ? Promise.resolve({ rows: [] as Question[] })
          : api<{ rows: Question[] }>(`/v1/questions?docId=${id}`).catch(() => ({ rows: [] })),
      ])
      // A response that lands after a newer draw started would paint an older
      // revision over a newer one and drag lastRev backwards with it, so that
      // the next genuine update no longer counts as a change.
      if (gen !== generation || disposed) return

      const rows = questionsRes.rows || []
      const questionKey = rows.map((q) => `${q.id}:${q.status}`).join(',')

      // Nothing to repaint: this revision is already on screen.
      //
      // This is only safe because BUG-002 moved the publish to *after* the
      // commit. An event announcing a revision that had not committed yet
      // would be followed by a fetch that still returns the old rev, and this
      // guard would swallow the one redraw that would have corrected it.
      //
      // Question state rides on its own events ('questions', 'answered') and
      // does not move node.rev, so it needs a comparison of its own -- a
      // rev-only check would freeze the pending blocks and their buttons.
      if (lastRev && node.rev <= lastRev && questionKey === lastQuestions) return

      const changed = lastRev > 0 && node.rev > lastRev
      lastRev = node.rev
      lastQuestions = questionKey

      anchor = captureScroll(bodyEl)
      questions = rows
      /*
       * The revision FIRST, then the blocks.
       *
       * The editor mounts when `blocks` arrives, and reads `rev` as it does.
       * Setting the blocks first meant it mounted believing the revision was
       * 0, so the real one looked like a remote change the moment it landed --
       * and the editor re-seated itself, taking the caret of anybody who had
       * already started typing. The browser suite caught it as
       * "Second para.Head".
       */
      docRev = node.rev
      blocks = node.content
      // Svelte flushes on a microtask, so the anchor can only be re-applied
      // once the patched DOM exists.
      loaded = true
      await tick()
      if (disposed) return
      restoreScroll(bodyEl, anchor)

      if (changed && bodyEl) {
        bodyEl.classList.remove('flash')
        void bodyEl.offsetWidth
        bodyEl.classList.add('flash')
      }
    }

    /**
     * One draw at a time, with a dirty flag for everything that arrives while
     * it runs. A burst of ten writes costs two renders and six requests rather
     * than ten renders and thirty, and no two responses can race. TASK-034.
     */
    function draw(): Promise<void> {
      if (inFlight) {
        dirty = true
        return inFlight
      }
      inFlight = (async () => {
        try {
          do {
            dirty = false
            await drawOnce()
          } while (dirty && !disposed)
        } finally {
          inFlight = null
        }
      })()
      return inFlight
    }

    function wire(source: EventSource) {
      source.onopen = () => {
        if (disposed) return
        status = 'live'
        live = true
        // Redraw on every (re)connect. Events published while the stream was
        // down are gone, so reconnecting is the only moment we can
        // resynchronise. BUG-002. The comment list resynchronises with it.
        draw().catch(() => {})
        commentsVersion += 1
        presenceVersion += 1
      }
      source.onerror = () => {
        if (disposed) return
        status = 'reconnecting'
        live = false
        // A ticket expires; re-mint rather than letting EventSource retry a
        // URL that can no longer authenticate.
        if (retry) clearTimeout(retry)
        retry = setTimeout(() => {
          if (!disposed) connect().catch(() => {})
        }, 3000)
      }
      source.onmessage = (e) => {
        if (disposed) return
        try {
          const event = JSON.parse(e.data)
          if (event.docId !== id) return
          live = true
          // Someone joined or left. The event carries no names (the stream
          // is open at `read`, the strip is not), so the strip is refetched
          // through its own gated route. Never a document draw, and not an
          // "updated" in the chip: nothing in the document changed.
          if (event.type === 'presence') {
            presenceVersion += 1
            return
          }
          status = event.type === 'rev' ? `rev ${event.rev}` : 'updated'
          // A comment or a thread changing state touches the sidebar and
          // nothing else: it moves no revision and no question, so a document
          // redraw would be a full fetch for a guaranteed no-op. TASK-041.
          if (event.type === 'comment' || event.type === 'comment_state') {
            commentsVersion += 1
            return
          }
          // An edit can move or detach anchors, and says so only with its
          // `rev` (TASK-030): re-read the comments with every one, including a
          // rev this page has already drawn -- the draw guard below is about
          // the document, not about the anchors that moved with it.
          if (event.type === 'rev') commentsVersion += 1
          // The event already carries the revision, so a rev we have painted
          // needs no fetch at all. Events without a rev ('questions',
          // 'answered') always draw.
          if (event.type === 'rev' && typeof event.rev === 'number' && event.rev <= lastRev) return
          draw().catch(() => {})
        } catch {
          /* a malformed frame is not worth tearing the stream down for */
        }
      }
    }

    async function connect() {
      /*
       * Not for a public reader. Live updates are a working feature -- a
       * ticket needs a principal, and the event stream carries comment and
       * question traffic that a stranger may not see. A published page is a
       * page: it is what it was when it was loaded, and reloading gets the
       * next version.
       */
      await whoami
      if (publicView === true || disposed) return
      // Exchange the bearer for a short-lived ticket; EventSource cannot send
      // headers, and a token in the URL reaches the logs. BUG-007.
      const { ticket } = await api<{ ticket: string }>(`/v1/events/${id}/ticket`, {
        method: 'POST',
      })
      if (disposed) return
      if (events) events.close()
      events = new EventSource(`/v1/events/${id}?ticket=${encodeURIComponent(ticket)}`)
      wire(events)
    }

    function fail(err: unknown) {
      if (disposed) return
      status = 'error'
      live = false

      if (isAuthError(err)) {
        title = 'Sign in'
        if (redirectToLogin()) return
        failure = { kind: 'stuck', message: 'This account cannot open that document.' }
        return
      }
      if (isMissingError(err)) {
        title = 'Unavailable'
        failure = { kind: 'missing' }
        return
      }
      // Anything else is most likely transient -- a dropped connection, a
      // server restarting -- so offer the one action that can help, rather
      // than leaving the header on "Loading…" and the body empty. TASK-034.
      title = 'Unavailable'
      failure = { kind: 'error', message: `Could not open this document: ${describeError(err)}.` }
    }

    // Hand the loop out, so a save can repaint without re-running this effect.
    redraw = () => void draw()

    draw()
      .then(() => {
        if (!disposed && !failure) return connect()
      })
      .catch(fail)

    return () => {
      disposed = true
      redraw = null
      if (retry) clearTimeout(retry)
      if (events) events.close()
      events = null
    }
  })

</script>

<svelte:head>
  <title>{title}</title>
  {#if publicView}
    <!-- The same answer the shell's `x-robots-tag` gives, for the crawlers
         that run JavaScript rather than read headers. Publishing means
         "anyone with the link", not "index this". -->
    <meta name="robots" content="noindex, nofollow" />
  {/if}
</svelte:head>

{#snippet strip()}
  {#if viewers}<ViewersStrip {viewers} />{/if}
{/snippet}

{#snippet docActions()}
  <button type="button" class="btn btn-quiet btn-small" onclick={() => (sharing = true)}>
    Share
  </button>
{/snippet}

<TopBar
  docActions={canShare ? docActions : undefined}
  back={publicView ? undefined : '/'}
  guest={publicView === true}
  docName={failure ? undefined : title}
  {status}
  {live}
  presence={viewers ? strip : undefined}
/>

<main>
  <div class="measure">
    {#if viewers && !failure}
      <!-- The same strip, for a bar too narrow to hold it; app.css shows one of the two. -->
      <div class="presence-row"><ViewersStrip {viewers} compact /></div>
    {/if}
    {#if failure?.kind === 'missing'}
      <p class="empty">This document doesn't exist, or you don't have access.</p>
      <p class="empty"><a href="/" onclick={(e) => navigate(e, '/')}>Back to all documents</a></p>
    {:else if failure?.kind === 'stuck'}
      <p class="empty">{failure.message}</p>
      <p class="empty"><a href="/login">Go to sign in</a></p>
    {:else if failure?.kind === 'error'}
      <p class="empty">{failure.message}</p>
      <div class="options">
        <button type="button" class="btn btn-quiet btn-small" onclick={() => location.reload()}>
          Try again
        </button>
      </div>
    {:else if noTabs}
      <p class="empty">This doc has no tabs yet.</p>
    {:else}
      <div class="doc-layout">
        <div class="doc-column">
          {#if canEdit && loaded}
            <!--
              ONE EDITOR OVER THE WHOLE DOCUMENT.

              Euan, having used the version that opened a paragraph at a time
              in a little box: "it's not really what I want the end product to
              be like. I want to edit the entire document without it being in
              a form, much like how Google Docs work... the tools sit at the
              top while you work."

              The read view below is what a viewer, a commenter and a public
              reader see, and it is still the only renderer for them.
            -->
            {#if editError}
              <p class="edit-error" role="alert">
                {editError}
                {#if detachPending}
                  <button type="button" class="linkish" onclick={() => void saveAnyway()}>
                    Save anyway
                  </button>
                {/if}
              </p>
            {/if}
            <div class="doc-body" bind:this={bodyEl}>
              <DocEditor
                blocks={blocks as any}
                rev={docRev}
                {locks}
                onsave={(ops) => sendOps(ops as any)}
                onblockchange={onCursorBlock}
              />
            </div>
          {:else}
            <div class="doc-body" bind:this={bodyEl}>
              {#each keyed as entry (entry.key)}
                {@const bid = entry.node.attrs?.id as string | undefined}
                {@const holder = locks.holderOf(bid)}
                <Block node={entry.node} {questions} onrendered={diagramSettled} />
                {#if holder}
                  <!--
                    A SIBLING, never a wrapper and never a child.

                    Not a wrapper: `.doc-body`'s direct children are the
                    blocks, and `captureScroll` walks them for `data-id` to
                    hold the reading position across a redraw.

                    Not a child: comment offsets are counted by walking the
                    text inside `[data-text-block]`, so anything in there
                    would shift every anchor in the paragraph.
                  -->
                  <div class="block-aside">
                    <span class="held-by">{holder} is editing</span>
                  </div>
                {/if}
              {/each}
            </div>
          {/if}
          {#if loaded && canComment}
            <SelectionComment root={bodyEl} blocks={scanned} oncomment={commentOn} />
          {/if}
          {#if !loaded}
            <div aria-hidden="true">
              <div class="skeleton-line skeleton-a"></div>
              <div class="skeleton-line skeleton-b"></div>
              <div class="skeleton-line skeleton-c"></div>
            </div>
          {/if}
        </div>
        {#if loaded && !publicView}
          <CommentSidebar
            bind:this={sidebar}
            {docId}
            {canComment}
            {isAgent}
            watching={viewers}
            refresh={commentsVersion}
            nodeId={tabNodeId}
            me={viewer}
            anchor={pendingAnchor}
            {diagrams}
            active={highlights.active}
            onclearanchor={() => (pendingAnchor = null)}
            onrows={(rows) => (commentRows = rows)}
            onactive={onThreadActive}
            onshow={showThread}
          />
        {/if}
      </div>
    {/if}
  </div>
</main>

{#if sharing}
  <!--
    The same dialog the document list opens, on the document itself. Its own
    component rather than a ModalSheet because it is not a confirm: it stays
    open while it works, one request per address with visible progress, and
    carries the access list underneath.
  -->
  <ShareDialog doc={shareDoc} onclose={() => (sharing = false)} />
{/if}

<style>
  /* ------------------------------------------------------ editing (TASK-050)
   *
   * The Edit button appears on hover and on keyboard focus, and it is
   * positioned OUTSIDE the text column so it can never sit under a word
   * somebody is trying to select for a comment. Those two gestures share this
   * surface, and the one that existed first does not get to be the one that
   * becomes awkward.
   */
  /*
   * Zero height, so the document's rhythm is exactly what it was without it,
   * and `position: relative` so the control inside can be lifted back onto
   * the line above.
   */
  .block-aside {
    position: relative;
    height: 0;
  }

  /* Revealed by hovering the paragraph ABOVE it, which is the block this
     control belongs to -- `:has` reads forwards, so the sibling combinator
     does the asking. */
  /* No hover on a touch screen, so the button would be unreachable. Shown
     always there, quietly, rather than hidden behind a gesture that does not
     exist on the device. */
  @media (hover: none) {
  }

  .held-by {
    position: absolute;
    bottom: 0.15rem;
    right: -9rem;
    width: 8rem;
    color: var(--color-red-deep, var(--color-red));
    font-family: var(--font-mono, monospace);
    font-size: 0.65rem;
    letter-spacing: 0.04em;
  }

  @media (max-width: 75rem) {
    /* No room in the margin: the marker goes above the block instead of
       beside it, and the Edit button sits inside the column's padding. */
    .held-by {
      right: 0;
      width: auto;
      /* Below the block rather than beside it, since there is no margin to
         sit in -- and the aside has no height, so this hangs into the gap the
         document already leaves between blocks. */
      bottom: -1.15rem;
    }
  }

  .edit-error {
    margin: 0.35rem 0 0.75rem;
    color: var(--color-red-deep, var(--color-red));
    font-size: 0.85rem;
  }

  /*
   * The document and its comments side by side where there is room for both,
   * stacked where there is not. The document column keeps `minmax(0, 1fr)` so
   * a wide table scrolls inside it instead of pushing the sidebar off screen.
   */
  .doc-layout {
    display: grid;
    gap: 3rem;

    /*
     * The comment highlight, from the palette: the warm plinth with a little
     * of the brand red in it -- warm, and plainly not the selection colour
     * (red over paper) or a browser's blue. The red itself only appears in the
     * underline of the thread that is active. Shared with the sidebar, which
     * draws its quotes in the same tint.
     */
    --anchor-tint: color-mix(in oklab, var(--color-red) 12%, var(--color-plinth));
    --anchor-tint-soft: color-mix(in oklab, var(--color-red) 5%, var(--color-plinth));
    --anchor-tint-strong: color-mix(in oklab, var(--color-red) 24%, var(--color-plinth));
    --anchor-edge: color-mix(in oklab, var(--color-red) 45%, var(--color-plinth));
  }

  /*
   * The strip's narrow-screen home: a row under the bar, right-aligned where
   * the bar's own strip would have been. Shown exactly where the bar's is not
   * (app.css, `.bar-presence`).
   */
  .presence-row {
    margin: calc(-1 * clamp(1rem, 4vw, 3rem)) 0 1.5rem;
  }

  @media (min-width: 64rem) {
    .presence-row {
      display: none;
    }
  }

  .doc-column {
    min-width: 0;
    /* The Comment button beside a selection is placed against this. */
    position: relative;
  }

  /*
   * Highlights. A thread's state is never told by colour alone: the underline
   * STYLE says it too -- solid for open, dashed for handled, dotted for a
   * closed thread (drawn only while its thread is hovered or focused) -- and
   * every range has a named pin button for the keyboard and screen readers.
   */
  .doc-body :global(mark.anchor) {
    color: inherit;
    background: var(--anchor-tint);
    border-radius: 2px;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-color: var(--anchor-edge);
    text-decoration-thickness: 2px;
    text-underline-offset: 0.2em;
    text-decoration-skip-ink: none;
    cursor: pointer;
    scroll-margin: 6rem 0;
    transition: background-color 0.12s ease;
  }

  .doc-body :global(mark.anchor[data-state='handled']) {
    background: var(--anchor-tint-soft);
    text-decoration-style: dashed;
  }

  .doc-body :global(mark.anchor[data-state='closed']) {
    background: none;
    text-decoration-style: dotted;
  }

  .doc-body :global(mark.anchor[data-active]) {
    background: var(--anchor-tint-strong);
    text-decoration-color: var(--color-red-deep);
  }

  .doc-body :global(.chip.anchored) {
    background: var(--anchor-tint);
    border-color: var(--anchor-edge);
  }

  /*
   * A diagram's comments (TASK-045). A label -- mermaid's HTML inside a
   * `foreignObject`, which clips to exactly the text -- is tinted with a
   * background and an INSET bottom edge; an outline or an underline would be
   * cut off. The edge carries the state as a highlight's underline does:
   * solid open, dashed handled, dotted closed (drawn as a gradient, since an
   * inset shadow has no dashes). `!important` because mermaid's own sheet
   * paints an edge label and its paragraph from an id selector. Anything else
   * on the diagram outlines the whole of it. Neither takes any room.
   */
  .doc-body :global(.anchor-label),
  .doc-body :global(.anchor-label p) {
    --label-edge: var(--anchor-edge);
    background: var(--anchor-tint) !important;
    box-shadow: inset 0 -2px var(--label-edge);
    transition: background-color 0.12s ease;
  }

  .doc-body :global(.anchor-label) {
    cursor: pointer;
    scroll-margin: 6rem 0;
  }

  .doc-body :global(.anchor-label[data-state='handled']),
  .doc-body :global(.anchor-label[data-state='handled'] p) {
    background: repeating-linear-gradient(90deg, var(--label-edge) 0 4px, transparent 4px 7px) bottom / 100% 2px
        no-repeat,
      var(--anchor-tint-soft) !important;
    box-shadow: none;
  }

  .doc-body :global(.anchor-label[data-state='closed']),
  .doc-body :global(.anchor-label[data-state='closed'] p) {
    background: repeating-linear-gradient(90deg, var(--label-edge) 0 2px, transparent 2px 4px) bottom / 100% 2px
        no-repeat !important;
    box-shadow: none;
  }

  .doc-body :global(.anchor-label[data-active]),
  .doc-body :global(.anchor-label[data-active] p) {
    --label-edge: var(--color-red-deep);
    background-color: var(--anchor-tint-strong) !important;
  }

  .doc-body :global(.mermaid[data-threads]) {
    outline: 2px solid var(--anchor-edge);
    outline-offset: 0.25rem;
    border-radius: 6px;
    cursor: pointer;
    scroll-margin: 6rem 0;
  }

  .doc-body :global(.mermaid[data-threads][data-state='handled']) {
    outline-style: dashed;
  }

  .doc-body :global(.mermaid[data-threads][data-state='closed']) {
    outline-style: dotted;
  }

  .doc-body :global(.mermaid[data-threads][data-active]) {
    outline-color: var(--color-red-deep);
  }

  /* The pin: a real button, out of sight until the keyboard reaches it. It has
     no text node on purpose (lib/selection.ts); its name is its aria-label and
     its visible face is generated. */
  .doc-body :global(.anchor-pin) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: 0;
    overflow: hidden;
    clip-path: inset(50%);
    border: 0;
    white-space: nowrap;
  }

  .doc-body :global(.anchor-pin:focus-visible) {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip-path: none;
    display: inline-block;
    margin: 0 0.125rem;
    padding: 0 0.375rem;
    vertical-align: 0.1em;
    font: 500 0.75rem/1.5 var(--font-sans);
    color: var(--paper);
    background: var(--color-red-deep);
    border-radius: 3px;
    cursor: pointer;
  }

  .doc-body :global(.anchor-pin:focus-visible)::after {
    content: 'Go to comment';
  }

  /* After a diagram, an inline pin would add a line: it sits over the
     diagram's top-right corner instead (TASK-045). */
  .doc-body :global(.diagram > .anchor-pin:focus-visible) {
    position: absolute;
    top: 0;
    right: 0;
    margin: 0;
  }

  @media (min-width: 64rem) {
    .doc-layout {
      grid-template-columns: minmax(0, 1fr) 20rem;
      align-items: start;
    }
  }
</style>
